import { FormSegmentedButton } from '@ipfabric/ui-components/FormSegmentedButton/FormSegmentedButton';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import type { SiteSeparationAttributeTransformation } from '@ipfabric/utils/schema/siteSeparation/siteSeparationAttributeTransformation';
import { useDiscoverySettingsContext } from '../../../composition/DiscoverySettings/discoverySettingsContext';
import { FormTagNameInput } from './FormTagNameInput';
import {
  CLOUD_RESOURCE_TAG_ORDER_DESCRIPTION,
  CLOUD_RESOURCE_TAG_SELECTED_TAGS_DESCRIPTION,
  CLOUD_RESOURCE_TAG_TRANSFORMATION_TOOLTIP_TEXT,
  type RuleEditorContentProps,
  type RuleEditorValues,
  TRANSFORMATION_OPTIONS,
} from './RuleEditor.types';
import { useCloudResourceTagOptions } from './useCloudResourceTagOptions';

export const CloudResourceTagRuleEditorContent = ({
  control,
}: RuleEditorContentProps) => {
  const { snapshotId } = useDiscoverySettingsContext();
  const {
    options: tagOptions,
    errorMessage: tagsError,
    loading: tagsLoading,
  } = useCloudResourceTagOptions(snapshotId);
  // Free-text picker, so the label/value pair collapses to the name itself.
  const tagSuggestions = tagOptions.map(({ value }) => value);

  return (
    <>
      <FormSegmentedButton.Single<
        RuleEditorValues,
        SiteSeparationAttributeTransformation
      >
        control={control}
        label='Text transformation'
        labelTooltip={CLOUD_RESOURCE_TAG_TRANSFORMATION_TOOLTIP_TEXT}
        name='transformation'
      >
        {TRANSFORMATION_OPTIONS.map(({ label, value }) => (
          <SegmentButton key={value} value={value} className='flex-1'>
            {label}
          </SegmentButton>
        ))}
      </FormSegmentedButton.Single>
      <FormTagNameInput
        control={control}
        dataTestId='site-separation-tag-names'
        description={CLOUD_RESOURCE_TAG_ORDER_DESCRIPTION}
        // Surfaced through the field's own error slot rather than a sibling
        // paragraph, so it is spaced and announced like any other field error.
        externalError={tagsError ?? undefined}
        label='Selected tag(s)'
        labelTooltip={CLOUD_RESOURCE_TAG_SELECTED_TAGS_DESCRIPTION}
        loading={tagsLoading}
        name='tagNames'
        suggestions={tagSuggestions}
      />
      <FormSwitch
        control={control}
        label='Include cloud devices discovered via SSH or Vendor API'
        name='applyToCloudInstances'
      />
      <FormTextArea
        control={control}
        dataTestId='site-separation-note'
        label='Rule description (optional)'
        name='note'
      />
    </>
  );
};
